import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface Report {
  period: string;
  filingDate: string;
  type: string;
  metrics: {
    revenue?: string | number | null;
    grossProfit?: string | number | null;
    operatingIncome?: string | number | null;
    netIncome?: string | number | null;
  };
}

interface Props {
  reports: Report[];
  compact?: boolean;
}

// Parse currency value to number (in billions)
const parseCurrency = (value?: string | number | null): number | null => {
  if (!value) return null;

  // If already a number, convert to billions
  if (typeof value === 'number') {
    return value / 1_000_000_000;
  }

  // If not a string, return null
  if (typeof value !== 'string') return null;

  // Remove $ and commas
  const clean = value.replace(/[$,]/g, '').trim();

  // Handle B for billions, M for millions
  if (clean.includes('B') || clean.includes('b')) {
    return parseFloat(clean.replace(/[Bb]/g, ''));
  } else if (clean.includes('M') || clean.includes('m')) {
    return parseFloat(clean.replace(/[Mm]/g, '')) / 1000;
  } else {
    const num = parseFloat(clean);
    return isNaN(num) ? null : num / 1_000_000_000;
  }
};

// Format number for display (in billions)
const formatBillions = (value: number | null): string => {
  if (value === null) return 'N/A';
  return `$${value.toFixed(2)}B`;
};

export default function IncomeStatementTrends({ reports, compact = false }: Props) {
  // State for toggling between Annual and Quarterly
  const [viewMode, setViewMode] = useState<'annual' | 'quarterly'>('annual');

  // Filter reports based on viewMode
  const filteredReports = reports.filter(r => {
    if (viewMode === 'annual') {
      return r.type === 'Annual' || r.period.includes('FY');
    } else {
      return r.type === 'Quarterly' || r.period.match(/Q[1-4]/);
    }
  });

  // Transform reports data for Recharts — skip reports with no financial data
  const chartData = filteredReports
    .map((report) => ({
      period: report.period,
      revenue: parseCurrency(report.metrics?.revenue),
      grossProfit: parseCurrency(report.metrics?.grossProfit),
      operatingIncome: parseCurrency(report.metrics?.operatingIncome),
      netIncome: parseCurrency(report.metrics?.netIncome),
    }))
    .filter((d) => d.revenue !== null || d.grossProfit !== null || d.operatingIncome !== null || d.netIncome !== null)
    .reverse(); // Reverse to show oldest to newest

  // Check if we have enough data
  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-neutral-900">
            Income Statement Trends
          </h3>
          <p className="text-sm text-neutral-600 mt-1">
            Historical performance across reporting periods
          </p>
        </CardHeader>
        <CardBody>
          <div className="text-center py-12 text-neutral-500">
            <p>No {viewMode} financial trend data available</p>
            <p className="text-sm mt-2">
              {viewMode === 'annual'
                ? 'Try switching to Quarterly view'
                : 'Try switching to Annual view'}
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-neutral-900`}>
              Income Statement Trends
            </h3>
            {!compact && (
              <p className="text-sm text-neutral-600 mt-1">
                Historical performance across reporting periods (in billions)
              </p>
            )}
          </div>
          {/* Toggle between Annual and Quarterly */}
          <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            <button
              onClick={() => setViewMode('annual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'annual'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Annual
            </button>
            <button
              onClick={() => setViewMode('quarterly')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'quarterly'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Quarterly
            </button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={compact ? 280 : 400}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis
              dataKey="period"
              stroke="#737373"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#737373"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `$${value.toFixed(1)}B`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e5e5',
                borderRadius: '6px',
                fontSize: '12px',
              }}
              formatter={(value: any) => formatBillions(value as number | null)}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Revenue"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="grossProfit"
              stroke="#10b981"
              strokeWidth={2}
              name="Gross Profit"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="operatingIncome"
              stroke="#14b8a6"
              strokeWidth={2}
              name="Operating Income"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="netIncome"
              stroke="#22c55e"
              strokeWidth={2}
              name="Net Income"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Summary Statistics */}
        <div className="mt-4 pt-3 border-t border-neutral-200">
          <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-2 md:grid-cols-4'} gap-3`}>
            {[
              { label: 'Revenue', key: 'revenue' as const, color: 'text-primary-600' },
              { label: 'Gross Profit', key: 'grossProfit' as const, color: 'text-green-600' },
              { label: 'Op. Income', key: 'operatingIncome' as const, color: 'text-teal-600' },
              { label: 'Net Income', key: 'netIncome' as const, color: 'text-green-500' },
            ].map(({ label, key, color }) => {
              const latest = chartData[chartData.length - 1]?.[key] as number | null;
              const previous = chartData.length >= 2 ? chartData[chartData.length - 2]?.[key] as number | null : null;
              const change = (latest != null && previous != null && previous !== 0)
                ? ((latest - previous) / Math.abs(previous)) * 100 : null;
              return (
                <div key={key}>
                  <div className={`${compact ? 'text-[10px]' : 'text-xs'} font-medium text-neutral-500`}>{label}</div>
                  <div className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${color} tabular-nums`}>
                    {formatBillions(latest)}
                  </div>
                  {change != null && (
                    <div className={`text-[10px] font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% vs prior
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
